import React, { useEffect, useState } from 'react';
import { ExternalLink, MessageSquare, RotateCcw, Save } from 'lucide-react';
import { adminFetch } from '../api';
import {
  BotMessageTemplate,
  DEFAULT_BOT_MESSAGE_TEMPLATE,
  MAP_BUTTON_TEXT_MAX,
  MapMode,
  PROMO_TEXT_MAX,
  TextStyle,
  normalizeBotMessageTemplate,
  renderPromoMessage,
  renderShiftCards,
  renderShiftMessage,
} from '../botMessage';

const ICON_OPTIONS = ['📍', '📅', '🕗', '🏥', '💊', '➕', '📞', '☎️', '🗺️', '📌', '✅', '🙌', '💚'];

const STYLE_LABELS: Record<TextStyle, string> = { normal: 'Normal', bold: 'Negrita', italic: 'Cursiva' };

const SAMPLE_DATA = {
  day: 'hoy',
  date: '15/09',
  pharmacies: [
    { nombre: 'Cicala', direccion: 'Quintana 620', telefono: '249 4360437', mapUrl: 'https://farmaciasdeturnotandil.com.ar/mapa/cicala' },
    { nombre: 'Sanchez', direccion: 'Av. M Actis 1738', telefono: '249 4630636', mapUrl: 'https://farmaciasdeturnotandil.com.ar/mapa/sanchez' },
  ],
};

type SectionKey = keyof BotMessageTemplate;

interface SectionConfig {
  key: SectionKey;
  label: string;
  hint?: string;
  hasText?: boolean;
  hasIcon?: boolean;
  hasStyle?: boolean;
  hasUppercase?: boolean;
  hasMapMode?: boolean;
}

const SECTIONS: SectionConfig[] = [
  { key: 'title', label: 'Título', hasText: true, hasIcon: true, hasStyle: true, hint: '{DIA} se reemplaza por HOY o MAÑANA.' },
  { key: 'date', label: 'Fecha y horario', hasText: true, hasIcon: true, hasStyle: true, hint: '{fecha} se reemplaza por el día y mes, ej. 15/09.' },
  { key: 'name', label: 'Nombre de la farmacia', hasIcon: true, hasStyle: true, hasUppercase: true },
  { key: 'address', label: 'Dirección', hasStyle: true, hint: 'Va en la misma línea que el nombre, después de un guion.' },
  { key: 'phone', label: 'Teléfono', hasIcon: true, hasStyle: true },
  { key: 'map', label: 'Mapa', hasIcon: true, hasMapMode: true },
  { key: 'footer', label: 'Frase final', hasText: true, hasIcon: true, hasStyle: true, hint: 'Dejala vacía para no mostrarla.' },
];

type SectionValue = Partial<{
  icon: string;
  style: TextStyle;
  text: string;
  uppercase: boolean;
  mode: MapMode;
  buttonText: string;
  enabled: boolean;
}>;

const PreviewBubble: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white rounded-lg text-[12px] leading-relaxed whitespace-pre-wrap break-words text-slate-800 shadow-xs overflow-hidden">
    {children}
  </div>
);

// Muestra *negrita* y _cursiva_ como las ve WhatsApp.
function WhatsAppText({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*[^*\n]+\*|_[^_\n]+_)/g).map((part, i) => {
        if (/^\*[^*\n]+\*$/.test(part)) return <strong key={i}>{part.slice(1, -1)}</strong>;
        if (/^_[^_\n]+_$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}

export function BotMessageEditor() {
  const [template, setTemplate] = useState<BotMessageTemplate>(DEFAULT_BOT_MESSAGE_TEMPLATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ success: boolean; text: string } | null>(null);

  useEffect(() => {
    adminFetch('/api/whatsapp/message-template')
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'No se pudo cargar la plantilla.');
        setTemplate(normalizeBotMessageTemplate(data));
      })
      .catch((err) => setMessage({ success: false, text: err?.message || 'No se pudo cargar la plantilla.' }))
      .finally(() => setLoading(false));
  }, []);

  const update = (key: SectionKey, patch: SectionValue) => {
    setTemplate((current) => ({ ...current, [key]: { ...current[key], ...patch } }) as BotMessageTemplate);
    setMessage(null);
  };

  const isButtonMode = template.map.mode === 'button';
  const promoText = renderPromoMessage(template);
  const promoInvalid = template.promo.enabled && !template.promo.text.trim();

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await adminFetch('/api/whatsapp/message-template', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(template),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'No se pudo guardar la plantilla.');
      setTemplate(normalizeBotMessageTemplate(data.template));
      setMessage({ success: true, text: 'Plantilla guardada. El bot ya responde con este formato.' });
    } catch (err: any) {
      setMessage({ success: false, text: err?.message || 'No se pudo guardar la plantilla.' });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-emerald-500';

  return (
    <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4 text-slate-900">
      <div className="flex items-center gap-2 border-b border-slate-150 pb-3">
        <MessageSquare size={16} className="text-emerald-500" />
        <h2 className="text-sm font-bold text-slate-800 font-sans">Mensaje de turnos del bot</h2>
      </div>
      <p className="text-[10px] text-slate-550 leading-relaxed font-medium">
        Elegí los íconos, la negrita o cursiva y la frase final. La vista previa usa farmacias de ejemplo.
      </p>

      <div className="rounded-xl bg-[#e5ddd5] p-3 space-y-2">
        {isButtonMode ? (
          renderShiftCards(template, SAMPLE_DATA).map((card, i) => (
            <PreviewBubble key={i}>
              <div className="p-3">
                <WhatsAppText text={card.body} />
              </div>
              <div className="border-t border-slate-100 py-2 text-center text-[12px] font-semibold text-sky-600 flex items-center justify-center gap-1.5">
                <ExternalLink size={12} />
                {card.buttonText}
              </div>
            </PreviewBubble>
          ))
        ) : (
          <PreviewBubble>
            <div className="p-3">
              <WhatsAppText text={renderShiftMessage(template, SAMPLE_DATA)} />
            </div>
          </PreviewBubble>
        )}
        {promoText && (
          <PreviewBubble>
            <div className="p-3">
              <WhatsAppText text={promoText} />
            </div>
          </PreviewBubble>
        )}
      </div>

      {loading ? (
        <p className="text-[11px] text-slate-500">Cargando plantilla...</p>
      ) : (
        <div className="space-y-2.5">
          {SECTIONS.map((section) => {
            const value = template[section.key] as SectionValue;
            return (
              <div key={section.key} className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                <p className="text-[11px] font-bold text-slate-700">{section.label}</p>

                {section.hasText && (
                  <input
                    type="text"
                    value={value.text ?? ''}
                    onChange={(e) => update(section.key, { text: e.target.value })}
                    className={`${inputClass} w-full`}
                  />
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {section.hasIcon && (
                    <>
                      <input
                        type="text"
                        aria-label={`Ícono de ${section.label}`}
                        placeholder="—"
                        value={value.icon ?? ''}
                        onChange={(e) => update(section.key, { icon: e.target.value })}
                        className={`${inputClass} w-12 text-center`}
                      />
                      <select
                        aria-label={`Elegir ícono de ${section.label}`}
                        value=""
                        onChange={(e) => update(section.key, { icon: e.target.value === 'none' ? '' : e.target.value })}
                        className={inputClass}
                      >
                        <option value="" disabled>Ícono…</option>
                        <option value="none">Sin ícono</option>
                        {ICON_OPTIONS.map((icon) => (
                          <option key={icon} value={icon}>{icon}</option>
                        ))}
                      </select>
                    </>
                  )}

                  {section.hasStyle && (
                    <select
                      aria-label={`Estilo de ${section.label}`}
                      value={value.style ?? 'normal'}
                      onChange={(e) => update(section.key, { style: e.target.value as TextStyle })}
                      className={inputClass}
                    >
                      {(Object.keys(STYLE_LABELS) as TextStyle[]).map((style) => (
                        <option key={style} value={style}>{STYLE_LABELS[style]}</option>
                      ))}
                    </select>
                  )}

                  {section.hasUppercase && (
                    <label className="flex items-center gap-1 text-[11px] text-slate-600 font-medium select-none">
                      <input
                        type="checkbox"
                        checked={!!value.uppercase}
                        onChange={(e) => update(section.key, { uppercase: e.target.checked })}
                      />
                      Mayúsculas
                    </label>
                  )}
                </div>

                {section.hasMapMode && (
                  <div className="space-y-2">
                    <select
                      aria-label="Cómo se muestra el mapa"
                      value={template.map.mode}
                      onChange={(e) => update('map', { mode: e.target.value as MapMode })}
                      className={`${inputClass} w-full`}
                    >
                      <option value="link">Link dentro del texto (un solo mensaje)</option>
                      <option value="button">Botón (un mensaje por farmacia)</option>
                    </select>
                    {template.map.mode === 'button' ? (
                      <>
                        <input
                          type="text"
                          aria-label="Texto del botón"
                          maxLength={MAP_BUTTON_TEXT_MAX}
                          value={template.map.buttonText}
                          onChange={(e) => update('map', { buttonText: e.target.value })}
                          className={`${inputClass} w-full`}
                        />
                        <p className="text-[10px] text-slate-400 font-normal">
                          Texto del botón, hasta {MAP_BUTTON_TEXT_MAX} caracteres ({template.map.buttonText.length}/{MAP_BUTTON_TEXT_MAX}).
                          El ícono no se usa con botón. Cada farmacia llega como un mensaje aparte.
                        </p>
                      </>
                    ) : (
                      <p className="text-[10px] text-slate-400 font-normal">Sin negrita ni cursiva, para que el link siga funcionando.</p>
                    )}
                  </div>
                )}

                {section.hint && <p className="text-[10px] text-slate-400 font-normal">{section.hint}</p>}
              </div>
            );
          })}

          <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-bold text-slate-700">Publicidad de cierre</p>
              <label className="flex items-center gap-1 text-[11px] text-slate-600 font-medium select-none">
                <input
                  type="checkbox"
                  checked={template.promo.enabled}
                  onChange={(e) => update('promo', { enabled: e.target.checked })}
                />
                Activa
              </label>
            </div>
            <textarea
              aria-label="Texto de la publicidad"
              rows={4}
              maxLength={PROMO_TEXT_MAX}
              value={template.promo.text}
              onChange={(e) => update('promo', { text: e.target.value })}
              className={`${inputClass} w-full resize-y`}
            />
            <p className={`text-[10px] font-normal ${promoInvalid ? 'text-rose-600' : 'text-slate-400'}`}>
              {promoInvalid
                ? 'Escribí el texto o desactivá la publicidad para poder guardar.'
                : 'Se manda como un mensaje aparte, después de las farmacias. Admite *negrita* y _cursiva_.'}{' '}
              ({template.promo.text.length}/{PROMO_TEXT_MAX})
            </p>
          </div>
        </div>
      )}

      {message && (
        <div
          className={`p-2.5 rounded-lg border text-center text-[11px] font-bold ${
            message.success ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-rose-50 border-rose-100 text-rose-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setTemplate(DEFAULT_BOT_MESSAGE_TEMPLATE); setMessage(null); }}
          disabled={loading || saving}
          className="px-3 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl text-xs font-bold flex items-center justify-center gap-1 cursor-pointer disabled:opacity-50"
        >
          <RotateCcw size={12} />
          Predeterminado
        </button>
        <button
          type="button"
          onClick={save}
          disabled={loading || saving || promoInvalid}
          className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
        >
          <Save size={12} />
          {saving ? 'Guardando...' : 'Guardar plantilla'}
        </button>
      </div>
    </section>
  );
}
